import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import * as OTPAuth from 'otpauth';
import { User, UserRole, LoginHistoryLog } from '../types';
import { getUsers, saveUsers, addUser as addUserToDb, deleteUser as deleteUserFromDb, updateUserPassword as updateUserPasswordInDb, updateUserPin as updateUserPinInDb, updateUserOtp, updateUserPushSubscription as updateUserPushSubscriptionInDb } from '../services/userService';
import { database } from '../services/firebase';
import { ref, onValue, set, get, push, serverTimestamp, remove, onDisconnect } from 'firebase/database';
import { subscribeUser, isPushSupported } from '../services/pushNotificationService';


export interface AppAssets {
  logo1?: string;
  logo2?: string;
  notificationSound?: string;
  notificationSound2?: string;
  crewIcon?: string;
  pilotIcon?: string;
  passengersIcon?: string;
  // New PWA Icons
  pwaIcon192?: string;
  pwaIcon512?: string;
  appleTouchIcon?: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<{ success: boolean; message: string; user?: User }>;
  verifyPinAndLogin: (user: User, pin: string) => Promise<{ success: boolean; message: string }>;
  verifyOtpAndLogin: (user: User, otpCode: string) => Promise<{ success: boolean; message: string }>;
  register: (username: string, password: string, email: string, authCode: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  users: User[];
  activeUsers: User[];
  isRegistrationOpen: boolean;
  toggleRegistration: () => void;
  authCodes: { [code: string]: UserRole };
  updateAuthCodes: (newCodes: { [code: string]: UserRole }) => void;
  deleteUser: (userId: string) => Promise<void>;
  createUser: (userData: Omit<User, 'id' | 'authCode'>) => Promise<{ success: boolean; message: string }>;
  securityScanPercentage: number;
  updateSecurityScanPercentage: (percentage: number) => Promise<void>;
  updateUserPassword: (userId: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; message: string }>;
  setPin: (password: string, newPin: string | null) => Promise<{ success: boolean; message: string }>;
  enableOtp: (password: string, secret: string, token: string) => Promise<{ success: boolean; message: string }>;
  disableOtp: (password: string) => Promise<{ success: boolean; message: string }>;
  appAssets: AppAssets;
  defaultAppAssets: AppAssets;
  updateAsset: (assetName: keyof AppAssets, dataUrl: string | null) => Promise<void>;
  vapidKey: string;
  updateVapidKey: (key: string) => Promise<void>;
  isMultiFlightEnabled: boolean;
  toggleMultiFlight: () => Promise<void>;
  updateUserPushSubscription: (subscription: PushSubscriptionJSON | null) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultAssets: AppAssets = {
  crewIcon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNMTUuNzUgNmEzLjc1IDMuNzUgMCAxIDEtNy41IDAgMy43NSAzLjc1IDAgMCAxIDcuNSAwWiIgLz48cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik00LjUgMTkuNS0zIDIyLjVtMTkuNS0zIDMgMyIgLz48cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik00LjUgMTkuNWE3LjUgNy41IDAgMCAxIDE1IDAiIC8+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNMTIgMTQuMjV2Mi4yNW0tMy0zLjc1di43NW02LTMuNzV2My43NSIgLz48cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Im05Ljc1IDEyLjc1IDIuMjUgMi4yNSAyLjI1LTIuMjUiIC8+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNMTIgMTV2NS4yNWEuNzUuNzUgMCAwIDEtLjc1Ljc1aC0zYS43NS43NSAwIDAgMS0uNzUtLjc1VjE1IiAvPjxwYXRoIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgZD0iTTEyIDE1djUuMjVhLjc1Ljc1IDAgMCAwIC43NS43NWgzYS43NS43NSAwIDAgMCAuNzUtLjc1VjE1IiAvPjwvc3ZnPg==',
  pilotIcon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNMTUuNzUgNmEzLjc1IDMuNzUgMCAxIDEtNy41IDAgMy43NSAzLjc1IDAgMCAxIDcuNSAwWiIgLz48cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik00LjUgMTkuNWE3LjUgNy41IDAgMCAxIDE1IDAiIC8+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNNy41IDEyLjc1aDkiIC8+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNNi4zNzUgOS4zNzVhMi42MjUgMi42MjUgMCAxIDEgNS4yNSAwIDIuNjI1IDIuNjI1IDAgMCAxLTUuMjUgMFptMTItMy4zNzVhMi42MjUgMi42MjUgMCAxIDAtNS4yNSAwIDIuNjI1IDIuNjI1IDAgMCAwIDUuMjUgMFoiIC8+PHBhdGggc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBkPSJNMTIgMTIuNzVWMjEiIC8+PC9zdmc+',
  passengersIcon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+CiAgICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik0xNSAxOS4xMjhhOS4zOCA5LjM4IDAgMDAyLjYyNS4zNzIgOS4zMzcgOS4zMzcgMCAwMDQuMTIxLS45NTIgNC4xMjUgNC4xMjUgMCAwMC03LjUzMy0yLjQ5M00xNSAxOS4xMjh2LS4wMDNjMC0xLjExMy0uMjg1LTIuMTYtLjc4Ni0zLjA3TTE1IDE5LjEyOHYuMTA2QTEyLjMxOCAxMi4zMTggMCAwMTguNjI0IDIxYy0yLjMzMSAwLTQuNTEyLS42NDUtNi4zNzQtMS43NjZsLS4wMDEtLjEwOWE2LjM3NSA2LjM3NSAwIDAxMTEuOTY0LTQuNjg0di4wMDV6TTE4IDE4YTEgMSAwIDAwLTEtMWgtMWExIDEgMCAxMDAgMmhhMSAxIDAgMDAxLTF6TTkgMTRhMSAxIDAgMDEtMSAxSDdhMSAxIDAgMTEwLTJoMWExIDEgMCAwMTEgMXoiIC8+CiAgICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik0xNSAxOS4xMjhhOS4zOCA5LjM4IDAgMDAyLjYyNS4zNzIgOS4zMzcgOS4zMzcgMCAwMDQuMTIxLS45NTIgNC4xMjUgNC4xMjUgMCAwMC03LjUzMy0yLjQ5M00xNSAxOS4xMjh2LS4wMDNjMC0xLjExMy0uMjg1LTIuMTYtLjc4Ni0zLjA3TTE1IDE5LjEyOHYuMTA2QTEyLjMxOCAxMi4zMTggMCAwMTguNjI0IDIxYy0yLjMzMSAwLTQuNTEyLS42NDUtNi4zNzQtMS43NjZsLS4wMDEtLjEwOWE2LjM3NSA2LjM3NSAwIDAxMTEuOTY0LTQuNjg0di4wMDV6TTkgMTIuNzVhMy4zNzUgMy4zNzUgMCAxMS02Ljc1IDAgMy4zNzUgMy4zNzUgMCAwMTYuNzUgMHoiIC8+Cjwvc3ZnPg==',
  pwaIcon192: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+CiAgICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik0xNSAxOS4xMjhhOS4zOCA5LjM4IDAgMDAyLjYyNS4zNzIgOS4zMzcgOS4zMzcgMCAwMDQuMTIxLS45NTIgNC4xMjUgNC4xMjUgMCAwMC03LjUzMy0yLjQ5M00xNSAxOS4xMjh2LS4wMDNjMC0xLjExMy0uMjg1LTIuMTYtLjc4Ni0zLjA3TTE1IDE5LjEyOHYuMTA2QTEyLjMxOCAxMi4zMTggMCAwMTguNjI0IDIxYy0yLjMzMSAwLTQuNTEyLS42NDUtNi4zNzQtMS43NjZsLS4wMDEtLjEwOWE2LjM3NSA2LjM3NSAwIDAxMTEuOTY0LTQuNjg0di4wMDV6TTE4IDE4YTEgMSAwIDAwLTEtMWgtMWExIDEgMCAxMDAgMmhhMSAxIDAgMDAxLTF6TTkgMTRhMSAxIDAgMDEtMSAxSDdhMSAxIDAgMTEwLTJoMWExIDEgMCAwMTEgMXoiIC8+CiAgICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik0xNSAxOS4xMjhhOS4zOCA5LjM4IDAgMDAyLjYyNS4zNzIgOS4zMzcgOS4zMzcgMCAwMDQuMTIxLS45NTIgNC4xMjUgNC4xMjUgMCAwMC03LjUzMy0yLjQ5M00xNSAxOS4xMjh2LS4wMDNjMC0xLjExMy0uMjg1LTIuMTYtLjc4Ni0zLjA3TTE1IDE5LjEyOHYuMTA2QTEyLjMxOCAxMi4zMTggMCAwMTguNjI0IDIxYy0yLjMzMSAwLTQuNTEyLS42NDUtNi4zNzQtMS43NjZsLS4wMDEtLjEwOWE2LjM3NSA2LjM3NSAwIDAxMTEuOTY0LTQuNjg0di4wMDV6TTkgMTIuNzVhMy4zNzUgMy4zNzUgMCAxMS02Ljc1IDAgMy4zNzUgMy4zNzUgMCAwMTYuNzUgMHoiIC8+Cjwvc3ZnPg==',
  pwaIcon512: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+CiAgICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik0xNSAxOS4xMjhhOS4zOCA5LjM4IDAgMDAyLjYyNS4zNzIgOS4zMzcgOS4zMzcgMCAwMDQuMTIxLS45NTIgNC4xMjUgNC4xMjUgMCAwMC03LjUzMy0yLjQ5M00xNSAxOS4xMjh2LS4wMDNjMC0xLjExMy0uMjg1LTIuMTYtLjc4Ni0zLjA3TTE1IDE5LjEyOHYuMTA2QTEyLjMxOCAxMi4zMTggMCAwMTguNjI0IDIxYy0yLjMzMSAwLTQuNTEyLS42NDUtNi4zNzQtMS43NjZsLS4wMDEtLjEwOWE2LjM3NSA2LjM3NSAwIDAxMTEuOTY0LTQuNjg0di4wMDV6TTE4IDE4YTEgMSAwIDAwLTEtMWgtMWExIDEgMCAxMDAgMmhhMSAxIDAgMDAxLTF6TTkgMTRhMSAxIDAgMDEtMSAxSDdhMSAxIDAgMTEwLTJoMWExIDEgMCAwMTEgMXoiIC8+CiAgICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik0xNSAxOS4xMjhhOS4zOCA5LjM4IDAgMDAyLjYyNS4zNzIgOS4zMzcgOS4zMzcgMCAwMDQuMTIxLS45NTIgNC4xMjUgNC4xMjUgMCAwMC03LjUzMy0yLjQ5M00xNSAxOS4xMjh2LS4wMDNjMC0xLjExMy0uMjg1LTIuMTYtLjc4Ni0zLjA3TTE1IDE5LjEyOHYuMTA2QTEyLjMxOCAxMi4zMTggMCAwMTguNjI0IDIxYy0yLjMzMSAwLTQuNTEyLS42NDUtNi4zNzQtMS43NjZsLS4wMDEtLjEwOWE2LjM3NSA2LjM3NSAwIDAxMTEuOTY0LTQuNjg0di4wMDV6TTkgMTIuNzVhMy4zNzUgMy4zNzUgMCAxMS02Ljc1IDAgMy4zNzUgMy4zNzUgMCAwMTYuNzUgMHoiIC8+Cjwvc3ZnPg==',
  appleTouchIcon: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAyNCAyNCIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZT0iI2ZmZiI+CiAgICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik0xNSAxOS4xMjhhOS4zOCA5LjM4IDAgMDAyLjYyNS4zNzIgOS4zMzcgOS4zMzcgMCAwMDQuMTIxLS45NTIgNC4xMjUgNC4xMjUgMCAwMC03LjUzMy0yLjQ5M00xNSAxOS4xMjh2LS4wMDNjMC0xLjExMy0uMjg1LTIuMTYtLjc4Ni0zLjA3TTE1IDE5LjEyOHYuMTA2QTEyLjMxOCAxMi4zMTggMCAwMTguNjI0IDIxYy0yLjMzMSAwLTQuNTEyLS42NDUtNi4zNzQtMS43NjZsLS4wMDEtLjEwOWE2LjM3NSA2LjM3NSAwIDAxMTEuOTY0LTQuNjg0di4wMDV6TTE4IDE4YTEgMSAwIDAwLTEtMWgtMWExIDEgMCAxMDAgMmhhMSAxIDAgMDAxLTF6TTkgMTRhMSAxIDAgMDEtMSAxSDdhMSAxIDAgMTEwLTJoMWExIDEgMCAwMTEgMXoiIC8+CiAgICA8cGF0aCBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGQ9Ik0xNSAxOS4xMjhhOS4zOCA5LjM4IDAgMDAyLjYyNS4zNzIgOS4zMzcgOS4zMzcgMCAwMDQuMTIxLS45NTIgNC4xMjUgNC4xMjUgMCAwMC03LjUzMy0yLjQ5M00xNSAxOS4xMjh2LS4wMDNjMC0xLjExMy0uMjg1LTIuMTYtLjc4Ni0zLjA3TTE1IDE5LjEyOHYuMTA2QTEyLjMxOCAxMi4zMTggMCAwMTguNjI0IDIxYy0yLjMzMSAwLTQuNTEyLS42NDUtNi4zNzQtMS43NjZsLS4wMDEtLjEwOWE2LjM3NSA2LjM3NSAwIDAxMTEuOTY0LTQuNjg0di4wMDV6TTkgMTIuNzVhMy4zNzUgMy4zNzUgMCAxMS02Ljc1IDAgMy4zNzUgMy4zNzUgMCAwMTYuNzUgMHoiIC8+Cjwvc3ZnPg==',
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [activeUsers, setActiveUsers] = useState<User[]>([]);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(true);
  const [authCodes, setAuthCodes] = useState<{ [code: string]: UserRole }>({});
  const [securityScanPercentage, setSecurityScanPercentage] = useState(10); // Default 10%
  const [appAssets, setAppAssets] = useState<AppAssets>(defaultAssets);
  const [vapidKey, setVapidKey] = useState('');
  const [isMultiFlightEnabled, setIsMultiFlightEnabled] = useState(true);

  const updateUserPushSubscription = useCallback(async (subscription: PushSubscriptionJSON | null) => {
    if (!user?.id) return;
    await updateUserPushSubscriptionInDb(user.id, subscription);
    
    setUser(currentUser => {
        if (!currentUser) return null;
        const updatedUser = { ...currentUser };
        if (subscription) {
            updatedUser.pushSubscription = subscription;
        } else {
            delete updatedUser.pushSubscription;
        }
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        return updatedUser;
    });
  }, [user?.id]);

  useEffect(() => {
    const initializeDefaultData = async () => {
      const usersSnapshot = await get(ref(database, 'users'));
      if (!usersSnapshot.exists()) {
        const defaultUsers: User[] = [
          { id: 'super_admin_permanent', username: 'JBP', password: 'J0hnismastr', role: UserRole.ADMIN, authCode: 'SUPER_ADMIN_SEED', pin: '1234' },
          { id: 'admin_demo', username: 'admin', password: 'admin', role: UserRole.ADMIN, authCode: 'ADMIN_SEED' },
          { id: 'supervisor_demo', username: 's', password: 's', role: UserRole.SUPERVISOR, authCode: 'SUPER' },
          { id: 'agent_demo', username: 'a', password: 'a', role: UserRole.AGENT, authCode: 'AGENT' },
          { id: 'viewer_demo', username: 'v', password: 'v', role: UserRole.VIEWER, authCode: 'VIEW' },
          { id: 'll_demo', username: 'll', password: 'll', role: UserRole.LL, authCode: 'LANDING' },
        ];
        await saveUsers(defaultUsers);
      }

      const regStatusSnapshot = await get(ref(database, 'config/registrationOpen'));
      if (!regStatusSnapshot.exists()) {
        await set(ref(database, 'config/registrationOpen'), true);
      }
      
      const multiFlightSnapshot = await get(ref(database, 'config/isMultiFlightEnabled'));
      if (!multiFlightSnapshot.exists()) {
        await set(ref(database, 'config/isMultiFlightEnabled'), true);
      }

      const authCodesSnapshot = await get(ref(database, 'config/authCodes'));
      if (!authCodesSnapshot.exists()) {
        const defaultCodes = {
            'SUPER': UserRole.SUPERVISOR,
            'AGENT': UserRole.AGENT,
            'VIEW': UserRole.VIEWER,
            'LANDING': UserRole.LL,
        };
        await set(ref(database, 'config/authCodes'), defaultCodes);
      }
      
      const flightNumbersSnapshot = await get(ref(database, 'config/flightNumbers'));
      if (!flightNumbersSnapshot.exists()) {
        const defaultFlights = ['JT001', 'JT002', 'JT003'];
        await set(ref(database, 'config/flightNumbers'), defaultFlights);
      }

      const scanPercentageSnapshot = await get(ref(database, 'config/securityScanPercentage'));
      if (!scanPercentageSnapshot.exists()) {
        await set(ref(database, 'config/securityScanPercentage'), 10);
      }
    };
    
    initializeDefaultData();

    // Set up listeners for real-time updates
    const usersRef = ref(database, 'users');
    const usersUnsubscribe = onValue(usersRef, (snapshot) => {
        const data = snapshot.val();
        setUsers(data ? Object.values(data) : []);
    });

    const regStatusRef = ref(database, 'config/registrationOpen');
    const regStatusUnsubscribe = onValue(regStatusRef, (snapshot) => {
        setIsRegistrationOpen(snapshot.val() ?? true);
    });

    const multiFlightRef = ref(database, 'config/isMultiFlightEnabled');
    const multiFlightUnsubscribe = onValue(multiFlightRef, (snapshot) => {
        setIsMultiFlightEnabled(snapshot.val() ?? true);
    });

    const authCodesRef = ref(database, 'config/authCodes');
    const authCodesUnsubscribe = onValue(authCodesRef, (snapshot) => {
        setAuthCodes(snapshot.val() ?? {});
    });

    const scanPercentageRef = ref(database, 'config/securityScanPercentage');
    const scanPercentageUnsubscribe = onValue(scanPercentageRef, (snapshot) => {
        setSecurityScanPercentage(snapshot.val() ?? 10);
    });

    const assetsRef = ref(database, 'config/assets');
    const assetsUnsubscribe = onValue(assetsRef, (snapshot) => {
        setAppAssets({...defaultAssets, ...(snapshot.val() ?? {})});
    });

    const vapidKeyRef = ref(database, 'config/vapidKey');
    const vapidKeyUnsubscribe = onValue(vapidKeyRef, (snapshot) => {
        setVapidKey(snapshot.val() ?? '');
    });


    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    return () => {
      usersUnsubscribe();
      regStatusUnsubscribe();
      authCodesUnsubscribe();
      scanPercentageUnsubscribe();
      assetsUnsubscribe();
      vapidKeyUnsubscribe();
      multiFlightUnsubscribe();
    };
  }, []);
  
    // Real-time presence system
  useEffect(() => {
    if (users.length === 0) return;

    const activeSessionsRef = ref(database, 'activeSessions');
    const unsubscribe = onValue(activeSessionsRef, (snapshot) => {
        const activeIds = Object.keys(snapshot.val() || {});
        const activeUsersList = users.filter(u => activeIds.includes(u.id));
        setActiveUsers(activeUsersList);
    });

    return () => unsubscribe();
  }, [users]);
  
  const logout = useCallback(async () => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
        const userToLogOut: User = JSON.parse(storedUser);
        const sessionRef = ref(database, `activeSessions/${userToLogOut.id}`);
        remove(sessionRef); // Fire-and-forget removal

        push(ref(database, 'loginHistory'), {
            userId: userToLogOut.id,
            username: userToLogOut.username,
            action: 'logout',
            timestamp: serverTimestamp()
        });
    }
    setUser(null);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('currentUserSessionId');
  }, []);

  // Session validation effect
  useEffect(() => {
    if (!user) return;

    const userSessionRef = ref(database, `activeSessions/${user.id}`);

    const establishOrValidateSession = async () => {
        const localSessionId = localStorage.getItem('currentUserSessionId');
        if (!localSessionId) {
          await logout();
          return;
        }
        
        const snapshot = await get(userSessionRef);
        const remoteSessionId = snapshot.val();
  
        if (remoteSessionId === null) {
          // Session expired or was cleared on disconnect. Re-establish it.
          console.log('Re-establishing session persistence.');
          await set(userSessionRef, localSessionId);
          onDisconnect(userSessionRef).remove();
        } else if (remoteSessionId !== localSessionId) {
          // A different session is active. This device should be logged out.
          await logout();
          alert("You have been signed out because you logged in on another device.");
        }
    };

    establishOrValidateSession();
  
    // This listener handles remote changes *while the app is running*.
    const unsubscribe = onValue(userSessionRef, (snapshot) => {
        const remoteSessionId = snapshot.val();
        const localSessionId = localStorage.getItem('currentUserSessionId');
        if (localSessionId && remoteSessionId && remoteSessionId !== localSessionId) {
            logout();
            alert("You have been signed out because you logged in on another device.");
        }
    });

    return () => unsubscribe();
  }, [user, logout]);

  const performLoginActions = useCallback(async (userToLogin: User) => {
    const newSessionId = `session_${Date.now()}`;
    const sessionRef = ref(database, `activeSessions/${userToLogin.id}`);
    let loggedInUser = { ...userToLogin };

    await set(sessionRef, newSessionId);
    onDisconnect(sessionRef).remove();

    localStorage.setItem('currentUserSessionId', newSessionId);

    // Sync push notifications on login to prevent loops from useEffect
    if (isPushSupported() && vapidKey) {
        if (Notification.permission === 'granted') {
            const registration = await navigator.serviceWorker.ready;
            const currentSubscription = await registration.pushManager.getSubscription();

            if (currentSubscription) {
                const currentSubJSON = currentSubscription.toJSON();
                if (JSON.stringify(loggedInUser.pushSubscription) !== JSON.stringify(currentSubJSON)) {
                    console.log('Syncing existing push subscription to DB on login.');
                    await updateUserPushSubscriptionInDb(loggedInUser.id, currentSubJSON);
                    loggedInUser.pushSubscription = currentSubJSON;
                }
            } else if (!loggedInUser.pushSubscription) {
                console.log('Permission granted, auto-subscribing user on login.');
                try {
                    const newSubscription = await subscribeUser(vapidKey);
                    const newSubJSON = newSubscription.toJSON();
                    await updateUserPushSubscriptionInDb(loggedInUser.id, newSubJSON);
                    loggedInUser.pushSubscription = newSubJSON;
                } catch (error) {
                    console.error('Failed to auto-subscribe user on login:', error);
                }
            }
        }
    }
    
    setUser(loggedInUser);
    localStorage.setItem('currentUser', JSON.stringify(loggedInUser));

    const loginLog: Omit<LoginHistoryLog, 'timestamp'> & { timestamp: object } = {
        userId: loggedInUser.id,
        username: loggedInUser.username,
        action: 'login',
        timestamp: serverTimestamp()
    };

    try {
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
            const ipData = await response.json();
            loginLog.ip = ipData.ip;
            loginLog.city = ipData.city;
            loginLog.region = ipData.region;
            loginLog.countryName = ipData.country_name;
        }
    } catch (error) {
        console.warn("Could not fetch IP location data:", error);
    }

    await push(ref(database, 'loginHistory'), loginLog);
  }, [vapidKey]);


  const login = useCallback(async (username: string, password: string): Promise<{ success: boolean; message: string; user?: User }> => {
    const foundUser = users.find(u => u.username.toLowerCase() === username.toLowerCase() && u.password === password);
    if (foundUser) {
        if (foundUser.otpEnabled && foundUser.otpSecret) {
            return { success: true, message: 'OTP required', user: foundUser };
        }
        if (foundUser.pin) {
            return { success: true, message: 'PIN verification required.', user: foundUser };
        } else {
            await performLoginActions(foundUser);
            return { success: true, message: 'Login successful.' };
        }
    }
    return { success: false, message: 'Invalid username or password.' };
  }, [users, performLoginActions]);

  const verifyPinAndLogin = useCallback(async (userToVerify: User, pin: string): Promise<{ success: boolean; message: string }> => {
    if (userToVerify.pin === pin) {
        await performLoginActions(userToVerify);
        return { success: true, message: 'Login successful' };
    }
    return { success: false, message: 'Invalid PIN.' };
  }, [performLoginActions]);

  const verifyOtpAndLogin = useCallback(async (userToVerify: User, otpCode: string): Promise<{ success: boolean; message: string }> => {
    if (!userToVerify.otpSecret || !userToVerify.otpEnabled) {
        return { success: false, message: 'OTP not configured for this user.' };
    }
    try {
        const totp = new OTPAuth.TOTP({
            secret: OTPAuth.Secret.fromBase32(userToVerify.otpSecret),
        });
        const delta = totp.validate({ token: otpCode, window: 1 });
        if (delta !== null) {
            await performLoginActions(userToVerify);
            return { success: true, message: 'Login successful' };
        }
        return { success: false, message: 'Invalid one-time code.' };
    } catch (error) {
        console.error("OTP validation error:", error);
        return { success: false, message: 'An error occurred during OTP validation.' };
    }
  }, [performLoginActions]);

  const register = useCallback(async (username: string, password: string, email: string, authCode: string): Promise<{ success: boolean; message: string }> => {
    if (users.some(u => u.username.toLowerCase() === username.toLowerCase())) {
        return { success: false, message: 'Username already exists.' };
    }
    
    const role = authCodes[authCode];
    if (!role) {
        return { success: false, message: 'Invalid Authorization Code.' };
    }

    const newUser: User = {
        id: `user_${Date.now()}`,
        username,
        password,
        email,
        role,
        authCode,
    };

    await addUserToDb(newUser);
    return { success: true, message: 'Registration successful! You can now log in.' };
  }, [users, authCodes]);
  
  const toggleRegistration = useCallback(async () => {
      const newStatus = !isRegistrationOpen;
      await set(ref(database, 'config/registrationOpen'), newStatus);
  }, [isRegistrationOpen]);
  
  const toggleMultiFlight = useCallback(async () => {
      const newStatus = !isMultiFlightEnabled;
      await set(ref(database, 'config/isMultiFlightEnabled'), newStatus);
  }, [isMultiFlightEnabled]);

  const updateAuthCodes = useCallback(async (newCodes: { [code: string]: UserRole }) => {
    await set(ref(database, 'config/authCodes'), newCodes);
  }, []);

  const deleteUser = useCallback(async (userId: string) => {
    await deleteUserFromDb(userId);
  }, []);
  
  const createUser = useCallback(async (userData: Omit<User, 'id' | 'authCode'>): Promise<{ success: boolean; message: string }> => {
    if (users.some(u => u.username.toLowerCase() === userData.username.toLowerCase())) {
        return { success: false, message: 'Username already exists.' };
    }

    const newUser: User = {
        id: `user_${Date.now()}`,
        username: userData.username,
        password: userData.password,
        email: userData.email,
        role: userData.role,
        authCode: 'CREATED_BY_ADMIN',
    };

    await addUserToDb(newUser);
    return { success: true, message: 'User created successfully.' };
  }, [users]);

  const updateSecurityScanPercentage = useCallback(async (percentage: number) => {
    await set(ref(database, 'config/securityScanPercentage'), percentage);
  }, []);

  const updateUserPassword = useCallback(async (userId: string, password: string) => {
    await updateUserPasswordInDb(userId, password);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Not logged in' };
    
    // Find the latest user data from the users list
    const currentUserData = users.find(u => u.id === user.id);
    if (!currentUserData || currentUserData.password !== currentPassword) {
        return { success: false, message: 'Current password is incorrect.' };
    }
    if (newPassword.length < 6) {
        return { success: false, message: 'New password must be at least 6 characters long.' };
    }

    await updateUserPasswordInDb(user.id, newPassword);
    
    const updatedUser = { ...user, password: newPassword };
    setUser(updatedUser);
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    return { success: true, message: 'Password updated successfully.' };
  }, [user, users]);

  const setPin = useCallback(async (password: string, newPin: string | null): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Not logged in' };
    
    const currentUserData = users.find(u => u.id === user.id);
    if (!currentUserData || currentUserData.password !== password) {
        return { success: false, message: 'Incorrect password.' };
    }
    if (newPin && !/^\d{4}$/.test(newPin)) {
        return { success: false, message: 'PIN must be exactly 4 digits.' };
    }

    await updateUserPinInDb(user.id, newPin);
    const updatedUser = { ...user, pin: newPin || undefined };
    setUser(updatedUser);
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    return { success: true, message: `Security PIN ${newPin ? 'updated' : 'removed'} successfully.` };
  }, [user, users]);
  
    const enableOtp = useCallback(async (password: string, secret: string, token: string): Promise<{ success: boolean; message: string }> => {
        if (!user) return { success: false, message: 'Not logged in' };
        
        const currentUserData = users.find(u => u.id === user.id);
        if (!currentUserData || currentUserData.password !== password) {
            return { success: false, message: 'Incorrect password.' };
        }

        try {
            const totp = new OTPAuth.TOTP({ secret: OTPAuth.Secret.fromBase32(secret) });
            const delta = totp.validate({ token, window: 1 });

            if (delta === null) {
                return { success: false, message: 'Invalid verification code.' };
            }

            await updateUserOtp(user.id, secret, true);
            const updatedUser = { ...user, otpSecret: secret, otpEnabled: true };
            setUser(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
            return { success: true, message: 'Authenticator app enabled successfully.' };
        } catch (error) {
            console.error('Error enabling OTP:', error);
            return { success: false, message: 'Failed to enable OTP.' };
        }
    }, [user, users]);

    const disableOtp = useCallback(async (password: string): Promise<{ success: boolean; message: string }> => {
        if (!user) return { success: false, message: 'Not logged in' };

        const currentUserData = users.find(u => u.id === user.id);
        if (!currentUserData || currentUserData.password !== password) {
            return { success: false, message: 'Incorrect password.' };
        }

        await updateUserOtp(user.id, null, false);
        const updatedUser = { ...user, otpSecret: undefined, otpEnabled: false };
        setUser(updatedUser);
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        return { success: true, message: 'Authenticator app disabled successfully.' };

    }, [user, users]);

  const updateAsset = useCallback(async (assetName: keyof AppAssets, dataUrl: string | null) => {
    // An explicit null means remove; we store it as an empty string to distinguish
    // from "not set", which should trigger the default asset.
    await set(ref(database, `config/assets/${assetName}`), dataUrl === null ? '' : dataUrl);
  }, []);

  const updateVapidKey = useCallback(async (key: string) => {
    await set(ref(database, 'config/vapidKey'), key);
  }, []);


  const contextValue = useMemo(() => ({
    user,
    login,
    verifyPinAndLogin,
    verifyOtpAndLogin,
    register,
    logout,
    users,
    activeUsers,
    isRegistrationOpen,
    toggleRegistration,
    authCodes,
    updateAuthCodes,
    deleteUser,
    createUser,
    securityScanPercentage,
    updateSecurityScanPercentage,
    updateUserPassword,
    changePassword,
    setPin,
    enableOtp,
    disableOtp,
    appAssets,
    defaultAppAssets: defaultAssets,
    updateAsset,
    vapidKey,
    updateVapidKey,
    isMultiFlightEnabled,
    toggleMultiFlight,
    updateUserPushSubscription,
  }), [
    user,
    login,
    verifyPinAndLogin,
    verifyOtpAndLogin,
    register,
    logout,
    users,
    activeUsers,
    isRegistrationOpen,
    toggleRegistration,
    authCodes,
    updateAuthCodes,
    deleteUser,
    createUser,
    securityScanPercentage,
    updateSecurityScanPercentage,
    updateUserPassword,
    changePassword,
    setPin,
    enableOtp,
    disableOtp,
    appAssets,
    updateAsset,
    vapidKey,
    updateVapidKey,
    isMultiFlightEnabled,
    toggleMultiFlight,
    updateUserPushSubscription
  ]);


  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
